// Display-only subscription helpers. The browser never grants PRO.
// Checkout verify and the Razorpay webhook write entitlement on the server.
import { apiUrl } from '../config/api';
import { getAuthToken } from './auth';

const ADMIN_EMAILS = [
  'admin@getbyonco.com',
  'care@getbyonco.com',
];

export const isAdmin = (user) => {
  if (!user) return false;
  if (user.role === 'admin') return true;
  const email = String(user.email || '').trim().toLowerCase();
  return ADMIN_EMAILS.includes(email);
};

function authHeaders() {
  const headers = { 'Content-Type': 'application/json' };
  const token = getAuthToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
}

/**
 * Fetch subscription status from the API. Fail closed. Never reads localStorage.
 */
export const getSubscriptionStatus = async (user) => {
  if (!user || !(user.email || user.id)) return null;
  try {
    const response = await fetch(apiUrl('/api/payments/subscription/status'), {
      credentials: 'include',
      headers: authHeaders(),
    });

    if (!response.ok) return null;

    const data = await response.json();
    if (data.isActive || data.has_subscription) {
      const stored = data.subscription || {};
      return {
        planId: data.plan || stored.plan_id || stored.planId || 'monthly',
        planKey: stored.plan_key || data.plan,
        expiresAt: data.expiresAt || stored.expires_at || stored.expiresAt,
        lifetime: !!data.lifetime || !!stored.lifetime,
        active: true,
        amount: data.amount,
        startedAt: data.startedAt || stored.subscribed_at || stored.created_at,
        automaticRenewal: false,
        isAdmin: !!data.isAdmin,
      };
    }
    return null;
  } catch {
    return null;
  }
};

export const isSubscriptionActive = (subscription) => {
  if (!subscription) return false;
  if (subscription.lifetime || subscription.plan_id === 'lifetime' || subscription.planId === 'lifetime') {
    return true;
  }
  const expiresAt = subscription.expiresAt || subscription.expires_at;
  if (!expiresAt) return false;
  return new Date(expiresAt) > new Date();
};

/**
 * Poll until the server has written entitlement, or give up.
 * Used after Razorpay checkout, never invents a local grant.
 */
export async function waitForEntitlement(user, { attempts = 8, delayMs = 750 } = {}) {
  for (let i = 0; i < attempts; i += 1) {
    const subscription = await getSubscriptionStatus(user);
    if (subscription && isSubscriptionActive(subscription)) return subscription;
    if (i < attempts - 1) {
      await new Promise((resolve) => setTimeout(resolve, delayMs));
    }
  }
  return null;
}

export const hasPaidAccess = async (user) => {
  if (isAdmin(user)) {
    return { hasAccess: true, reason: 'admin' };
  }
  const subscription = await getSubscriptionStatus(user);
  if (!subscription) {
    return { hasAccess: false, reason: 'no_subscription' };
  }
  if (!isSubscriptionActive(subscription)) {
    return { hasAccess: false, reason: 'expired' };
  }
  return { hasAccess: true, reason: 'active_subscription', subscription };
};

export const getDaysRemaining = (subscription) => {
  if (!subscription || !(subscription.expiresAt || subscription.expires_at)) return 0;
  const expiryDate = new Date(subscription.expiresAt || subscription.expires_at);
  const diffDays = Math.ceil((expiryDate - new Date()) / (1000 * 60 * 60 * 24));
  return Math.max(0, diffDays);
};

export const getSubscriptionState = async (user) => {
  if (isAdmin(user)) {
    return {
      isActive: true,
      expiresAt: null,
      daysLeft: null,
      isAdmin: true,
      planLabel: 'Included',
      planId: 'admin',
      automaticRenewal: false,
    };
  }
  const subscription = await getSubscriptionStatus(user);
  if (!subscription) {
    return { isActive: false, expiresAt: null, daysLeft: null };
  }
  if (subscription.lifetime || subscription.planId === 'lifetime' || subscription.plan_id === 'lifetime') {
    return {
      isActive: true,
      expiresAt: null,
      daysLeft: null,
      lifetime: true,
      planLabel: subscription.amount || '$999 lifetime',
      planId: 'lifetime',
      startedAt: subscription.startedAt,
      automaticRenewal: false,
    };
  }
  const expiresAt = subscription.expiresAt || subscription.expires_at;
  if (!expiresAt && !subscription.active) {
    return { isActive: false, expiresAt: null, daysLeft: null };
  }
  let expiryDate = null;
  if (expiresAt) {
    try {
      expiryDate = new Date(typeof expiresAt === 'string' ? expiresAt.replace('Z', '+00:00') : expiresAt);
    } catch {
      expiryDate = null;
    }
  }
  const isActive = !!subscription.active && (!expiryDate || expiryDate > new Date());
  let daysLeft = null;
  if (isActive && expiryDate) {
    daysLeft = Math.max(0, Math.ceil((expiryDate - new Date()) / (1000 * 60 * 60 * 24)));
  }
  return {
    isActive,
    expiresAt: expiryDate ? expiresAt : null,
    daysLeft,
    planLabel: subscription.amount,
    planId: subscription.planKey || subscription.planId,
    startedAt: subscription.startedAt,
    automaticRenewal: false,
  };
};

export const clearSubscription = () => {
  localStorage.removeItem('subscription_status');
  localStorage.removeItem('byonco_subscription_status');
};
